import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { registerAction } from './actionRegistry';

// "View scores" — navigates to the score form for a candidate. Ported from
// the old RequirementPipeline.jsx's handleShowScores; only meaningful once a
// candidate has actually taken (or attempted) the test, so — matching the
// old behavior — this renders nothing for any other status.
//
// NOTE: the flow-statuses API (GET /assignments/:id/flow-statuses) returns
// tab/status labels as Title Case ("In Progress", "Passed", "Failed"), but
// it's unconfirmed whether an individual candidate row's own `status` field
// (from LISTBYSTATUSFORTA) uses that same casing or the old lowercase
// enum ('in_progress') — the old RequirementPipeline.jsx predates this flow system
// and used the latter. Normalizing both sides avoids silently hiding this
// button if the real value turns out to be differently cased/punctuated.
const normalizeStatus = (value) =>
  (value || '').toLowerCase().replace(/[^a-z]/g, '');

const SCORABLE_STATUSES = ['inprogress', 'passed', 'failed'];
const READONLY_STATUSES = ['passed', 'failed'];

const ViewScoresAction = ({
  candidate,
  opportunityId,
  status,
  assignmentId,
  requirementLocationId,
}) => {
  const navigate = useNavigate();

  if (!SCORABLE_STATUSES.includes(normalizeStatus(candidate.status)))
    return null;

  const handleClick = (e) => {
    e.stopPropagation();
    const firstName =
      candidate.first_name ||
      (candidate.candidate_name ? candidate.candidate_name.split(' ')[0] : '');
    const lastName =
      candidate.last_name ||
      (candidate.candidate_name
        ? candidate.candidate_name.split(' ').slice(1).join(' ')
        : '');
    const isReadonly = READONLY_STATUSES.includes(
      normalizeStatus(candidate.status)
    );

    const params = new URLSearchParams({
      candidateId: candidate.candidate_id,
      opportunityId,
      status,
      firstName,
      lastName,
      email: candidate.email || '',
      phone: candidate.phone || '',
    });
    if (assignmentId) params.set('assignmentId', assignmentId);
    if (requirementLocationId)
      params.set('requirementLocationId', requirementLocationId);
    if (isReadonly) params.set('readonly', 'true');

    navigate(`/candidates/update-score?${params.toString()}`);
  };

  return (
    <Button variant="ghost" size="icon" onClick={handleClick}>
      <ClipboardCheck className="h-5 w-5 text-primary" />
    </Button>
  );
};

ViewScoresAction.propTypes = {
  candidate: PropTypes.shape({
    candidate_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    candidate_name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  status: PropTypes.string,
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requirementLocationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

ViewScoresAction.defaultProps = {
  opportunityId: undefined,
  status: undefined,
  assignmentId: undefined,
  requirementLocationId: undefined,
};

registerAction({
  stateMachineKey: 'default',
  actionKey: 'view_scores',
  component: ViewScoresAction,
});

export default ViewScoresAction;
